import { ReactNode } from "react";

import {
  formatFittedRange,
  useCollectionTimeRange,
} from "@/common/allRecordsRange";
import { HGCollection } from "@/common/types";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip";

// The shadcn primitive self-provides at delayDuration 0, which across a list of
// rows would fire a request the moment the pointer crossed one.
const HOVER_DELAY_MS = 400;

/**
 * The time range of one collection, fetched only while its tooltip is open.
 *
 * Radix keeps the content unmounted until then, so hovering a row is what asks
 * the server and nothing is requested on load. RTK Query caches per
 * `{userID, collectionID}`, so re-hovering costs nothing, and the entry is
 * shared with the "fit to all records" click on the open collection.
 */
function TimeRange({ collection }: { collection: HGCollection }) {
  const { target, isFetching } = useCollectionTimeRange(collection.id);

  if (target) {
    return (
      <div className="font-mono text-[11px] tabular-nums">
        {formatFittedRange(target)}
      </div>
    );
  }
  // Nothing rather than a spinner: the tooltip is already placed and would jump.
  if (isFetching) return null;

  return <div className="text-[11px] opacity-70">No dated records</div>;
}

/**
 * Wraps a collection row in its tooltip: the full title (which the row itself
 * truncates), when its records live, and why it is nested where it is.
 */
export default function CollectionRowTooltip({
  collection,
  count,
  children,
}: {
  collection: HGCollection;
  /** Records in the current interval, when known. */
  count?: number;
  children: ReactNode;
}) {
  return (
    <Tooltip delayDuration={HOVER_DELAY_MS}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      {/* Left, because this list lives in the right sidebar. */}
      <TooltipContent side="left" align="start" className="max-w-[240px]">
        <div className="flex flex-col gap-0.5">
          <div className="font-medium">{collection.title}</div>
          <TimeRange collection={collection} />
          {count !== undefined && (
            <div className="text-[11px] opacity-70">
              {count.toLocaleString()} {count === 1 ? "record" : "records"} in
              view
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
